import { useIsSpeaking, useParticipants, useTracks } from '@livekit/components-react'
import { type Participant, Track } from 'livekit-client'
import { useEffect, useMemo, useRef } from 'react'

import type { MicAccess } from './deviceProbe'
import { t } from './i18n'
import type { ModerationAction } from './moderation'
import { isLiveScreenShare } from './screenShare'
import { TileMenu } from './TileMenu'
import type { SharePending } from './useClassroomSignals'
import { isParent, isTeacher } from './useClassroomSignals'

/**
 * Who is in the class, for everyone in the class.
 *
 * Students get this too, not just the teacher. In a group lesson the grid can be
 * a filmstrip of six thumbnails while someone shares their screen, and a child
 * genuinely cannot tell who else is there — which matters to a ten-year-old more
 * than it does to us. It is also the only place the raised-hand QUEUE is legible:
 * hands are timestamped, so the panel can say who asked first, and a teacher can
 * take questions in a fair order instead of whoever they happen to notice.
 *
 * Hidden staff observers do not appear here, and that is not an omission: their
 * token sets `hidden: true`, so LiveKit never delivers them as participants to
 * anyone. The list is exactly the people who can be seen and heard.
 *
 * Positioned absolutely inside `.classroom` rather than being a flex row, so
 * opening it slides over the lesson instead of squeezing the video — the same
 * rule the toasts and the leave dialog follow.
 */

interface Props {
  onClose: () => void
  /** identity → epoch ms the hand went up. */
  hands: Record<string, number>
  /** Present only for a teacher; students get a read-only list. */
  onModerate?: (identity: string, action: ModerationAction) => Promise<void>
  onAsk?: (identity: string, source: 'microphone' | 'camera') => void
  onAskShare?: (identity: string) => void
  /** Identities whose device advertised it can capture a screen. */
  canShare: Record<string, boolean>
  micAccess: Record<string, MicAccess>
  sharePending: Record<string, SharePending>
  /** Someone is already presenting — one share at a time. */
  shareBusy: boolean
  /** The control that opened this, so focus can go back where it came from. */
  returnFocusTo?: React.RefObject<HTMLButtonElement>
}

export function Roster({
  onClose,
  hands,
  onModerate,
  onAsk,
  onAskShare,
  canShare,
  micAccess,
  sharePending,
  shareBusy,
  returnFocusTo,
}: Props) {
  const participants = useParticipants()
  // Subscribed purely to re-render on publish/mute changes: the mic and camera
  // state below is read from the participant getters, and `useParticipants` alone
  // does not re-render when someone mutes. Same source the tiles use, so the
  // panel and the tiles can never disagree.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.Microphone, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  )

  const sharingIdentities = useMemo(
    () => new Set(tracks.filter(isLiveScreenShare).map((track) => track.participant.identity)),
    [tracks],
  )

  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  /** Pull focus back to the People button only on keyboard dismissal. */
  const restoreFocus = useRef(true)
  // `onClose` is an inline arrow at the call site, so depending on it would
  // re-run this effect on every parent render and re-steal focus.
  const closeHandler = useRef(onClose)
  closeHandler.current = onClose

  useEffect(() => {
    restoreFocus.current = true
    closeRef.current?.focus()

    const onKey = (event: KeyboardEvent) => {
      // A row's moderation menu swallows Escape in the capture phase, so this
      // only ever fires when nothing inside the panel is open.
      if (event.key === 'Escape') closeHandler.current()
    }
    /**
     * Tapping the lesson dismisses this too.
     *
     * The X in the corner is the discoverable way out, but on a phone this is a
     * bottom sheet over most of the screen, and "tap outside to dismiss" is the
     * gesture a child will try first. A row's moderation menu is a DOM descendant
     * of this panel even though it is fixed-positioned, so interacting with it
     * counts as inside and does not close the list underneath it.
     */
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target)) return
      // The People button toggles on its own click; closing on the way down here
      // would let it reopen on the way up.
      if (returnFocusTo?.current?.contains(target)) return
      restoreFocus.current = false
      closeHandler.current()
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      // Focus goes back to the People button, but only for keyboard dismissal:
      // without this the focused close button unmounts and focus falls to
      // <body>, dropping a keyboard or TalkBack user at the top of the page
      // mid-lesson. On a tap elsewhere, stealing focus would be the rude option.
      if (restoreFocus.current) returnFocusTo?.current?.focus()
    }
    // The mount/unmount is now owned by the call site (`{rosterOpen ? <Roster
    // ... /> : null}` in Classroom.tsx), so the effect runs once on mount and
    // the cleanup runs once on unmount. No dep on `open` — it cannot change
    // while the panel is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable
  }, [])

  /**
   * Teacher first, then raised hands oldest-first, then everyone else by name.
   *
   * The hand ordering is the point of the sort: a queue that reshuffles as people
   * mute and unmute is worse than no queue, so position here is decided only by
   * when the hand went up.
   */
  const ordered = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' })
    return [...participants].sort((a, b) => {
      const aTeacher = isTeacher(a.identity)
      const bTeacher = isTeacher(b.identity)
      if (aTeacher !== bTeacher) return aTeacher ? -1 : 1

      const aHand = hands[a.identity] ?? 0
      const bHand = hands[b.identity] ?? 0
      if (Boolean(aHand) !== Boolean(bHand)) return aHand ? -1 : 1
      if (aHand && bHand) return aHand - bHand

      return collator.compare(a.name || a.identity, b.name || b.identity)
    })
  }, [participants, hands])

  const handOrder = useMemo(() => {
    const raised = Object.entries(hands)
      .filter(([, at]) => at > 0)
      .sort(([, a], [, b]) => a - b)
    return new Map(raised.map(([identity], index) => [identity, index + 1]))
  }, [hands])

  return (
    <div
      className="roster"
      ref={panelRef}
      role="dialog"
      aria-label={t('roster.title')}
      aria-modal="false"
    >
      <header className="roster__head">
        <h2 className="roster__title">
          {t('roster.title')}
          <span className="roster__count">{participants.length}</span>
        </h2>
        <button
          type="button"
          className="roster__close"
          onClick={onClose}
          ref={closeRef}
          aria-label={t('roster.close')}
          title={t('roster.close')}
        >
          <CloseIcon />
        </button>
      </header>

      {handOrder.size > 0 ? (
        <p className="roster__hint" role="status">
          ✋ {t('roster.handsUp').replace('{count}', String(handOrder.size))}
        </p>
      ) : null}

      <ul className="roster__list">
        {ordered.map((participant) => (
          <Row
            key={participant.identity}
            participant={participant}
            handPosition={handOrder.get(participant.identity)}
            sharing={sharingIdentities.has(participant.identity)}
            onModerate={onModerate}
            onAsk={onAsk}
            onAskShare={onAskShare}
            canShare={canShare}
            micAccess={micAccess}
            sharePending={sharePending}
            shareBusy={shareBusy}
          />
        ))}
      </ul>
    </div>
  )
}

function Row({
  participant,
  handPosition,
  sharing,
  onModerate,
  onAsk,
  onAskShare,
  canShare,
  micAccess,
  sharePending,
  shareBusy,
}: {
  participant: Participant
  handPosition?: number
  sharing: boolean
  onModerate?: (identity: string, action: ModerationAction) => Promise<void>
  onAsk?: (identity: string, source: 'microphone' | 'camera') => void
  onAskShare?: (identity: string) => void
  canShare: Record<string, boolean>
  micAccess: Record<string, MicAccess>
  sharePending: Record<string, SharePending>
  shareBusy: boolean
}) {
  // One hook per row, which is why each row is its own component — calling it in
  // a loop would break the hook rules the moment someone joins or leaves.
  //
  // `&& !micMuted` for the same reason as the tile's ring: LiveKit measures
  // speech per participant across ALL their audio tracks, so a share's soundtrack
  // would keep "Speaking" pinned next to a presenter's name for the length of a
  // video. It also fixes a plain contradiction the row could already show — a
  // muted-microphone icon beside the word "Speaking".
  const micMuted = !participant.isMicrophoneEnabled
  const speaking = useIsSpeaking(participant) && !micMuted
  const teacher = isTeacher(participant.identity)
  const parent = isParent(participant.identity)
  // Two different questions, and conflating them is what cost a class 19 minutes
  // on 2026-08-02: "did they choose to mute" versus "can their browser give them
  // a microphone at all". A missing attribute means we do not know, which must
  // read as plain muted rather than as a confident claim either way.
  const access = micAccess?.[participant.identity]
  const micBroken =
    access === 'blocked' || access === 'missing' || access === 'busy' || access === 'silent'
  const micReason = micBroken
    ? access === 'missing'
      ? t('roster.micMissing')
      : access === 'busy'
        ? t('roster.micBusy')
        : // A live microphone producing nothing. Worth its own words to a teacher:
          // "blocked" would send them to coach a permission that is already
          // granted, and the plain mute icon is what cost a lesson on 2026-08-02.
          access === 'silent'
          ? t('roster.micSilent')
          : t('roster.micBlocked')
    : null
  const cameraOff = !participant.isCameraEnabled
  const name = participant.name || participant.identity

  // Same rule as the tiles: never over yourself, never over staff. The server
  // refuses both anyway, so offering them would only produce a dead control.
  const moderatable = Boolean(onModerate) && !participant.isLocal && !teacher

  return (
    <li className={`roster__row ${speaking ? 'roster__row--speaking' : ''}`}>
      <span
        className={`roster__avatar ${teacher ? 'roster__avatar--teacher' : ''}`}
        aria-hidden="true"
      >
        {initialsFor(name)}
      </span>

      <span className="roster__who">
        <span className="roster__name">
          <bdi dir="auto">{name}</bdi>
          {participant.isLocal ? <span className="roster__you">{t('room.you')}</span> : null}
        </span>
        <span className="roster__meta">
          {teacher ? (
            <span className="badge badge--teacher">{t('role.teacher')}</span>
          ) : parent ? (
            <span className="badge badge--parent">{t('role.parent')}</span>
          ) : null}
          {handPosition ? (
            <span className="roster__hand" title={t('room.handsUp')}>
              ✋ {handPosition}
            </span>
          ) : null}
          {micReason ? <span className="roster__mic-warn">{micReason}</span> : null}
          {sharing ? <span className="roster__sharing">{t('room.presenting')}</span> : null}
          {speaking ? <span className="roster__talking">{t('roster.speaking')}</span> : null}
        </span>
      </span>

      {/* State icons carry a text label for screen readers: colour and glyph alone
          do not survive a colour-blind or non-visual read. */}
      <span className="roster__state">
        <span
          className={`roster__icon ${micMuted ? 'roster__icon--off' : ''} ${micBroken ? 'roster__icon--broken' : ''}`}
          title={micReason ?? undefined}
        >
          {micMuted ? <MicOffIcon /> : <MicIcon />}
          <span className="visually-hidden">
            {micReason ?? t(micMuted ? 'room.muted' : 'roster.micOn')}
          </span>
        </span>
        <span className={`roster__icon ${cameraOff ? 'roster__icon--off' : ''}`}>
          {cameraOff ? <CamOffIcon /> : <CamIcon />}
          <span className="visually-hidden">
            {t(cameraOff ? 'prejoin.cameraOff' : 'roster.cameraOn')}
          </span>
        </span>
      </span>

      {moderatable && onModerate && onAsk ? (
        <TileMenu
          name={name}
          micMuted={micMuted}
          micReason={micReason}
          cameraOff={cameraOff}
          sharing={sharing}
          onModerate={(action) => onModerate(participant.identity, action)}
          onAsk={(source) => onAsk(participant.identity, source)}
          onAskShare={onAskShare ? () => onAskShare(participant.identity) : undefined}
          isParent={parent}
          canShare={Boolean(canShare[participant.identity])}
          sharePending={sharePending[participant.identity]}
          shareBusy={shareBusy}
        />
      ) : null}
    </li>
  )
}

function initialsFor(name: string): string {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('') || '?'
  )
}

/* Inline icons — the CSP allows no third-party origin, and an icon font would be
   another request on a Cairo mobile connection. */
const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V22h2v-3.08A7 7 0 0 0 19 12h-2Z" />
  </svg>
)
const MicOffIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M15 10.6V6a3 3 0 0 0-5.94-.6l5.94 5.2ZM4.3 3 3 4.3l6 5.3V12a3 3 0 0 0 4.6 2.55l1.4 1.23A5 5 0 0 1 7 12H5a7 7 0 0 0 6 6.92V22h2v-3.08a7 7 0 0 0 3.2-1.35L19.7 20 21 18.7 4.3 3Z" />
  </svg>
)
const CamIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z" />
  </svg>
)
const CamOffIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M4.3 3 3 4.3 5.7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 .9-.6L19.7 21 21 19.7 4.3 3ZM17 8.5V7a1 1 0 0 0-1-1H9.5l7.5 7.4V13l4 4V6l-4 2.5Z" />
  </svg>
)
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 5.7 18.3l-1.4-1.4L10.6 12 4.3 5.7l1.4-1.4L12 10.6l4.9-4.9 1.4 1.4Z" />
  </svg>
)
